import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    Tabs,
    Tab,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import surveyService from '@/shared/services/surveyService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { useTerm } from '@/shared/contexts/TermContext';
import './surveys.css';
import { badgeTextFor } from '@/shared/utils/badgeText';

const PROGRAM_CONFIG = {
    si: {
        bg: 'success',
        label: 'SI',
        full: 'Supplemental Instruction',
    },
    ta: {
        bg: 'primary',
        label: 'TA',
        full: 'Teaching Assistant',
    },
    la: {
        bg: '',
        label: 'LA',
        full: 'Learning Assistant',
        className: 'survey-la-badge',
    },
};

const AUDIENCE_CONFIG = {
    faculty: { bg: 'info', label: 'Faculty' },
    students: { bg: 'warning', label: 'Students' },
    peer_supporters: { bg: 'secondary', label: 'Peer Supporters' },
    all: { bg: 'dark', label: 'All' },
};

function SurveyListPage() {
    const { userProfile, effectiveRoles } = useAuth();
    const { currentTermCode, termsLoading } = useTerm();
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('available');

    const userRoles = resolveRoles({ effectiveRoles, userProfile });
    const isCoordinator = userRoles.some((role) =>
        [
            'si_coordinator',
            'ta_coordinator',
            'la_coordinator',
            'admin',
        ].includes(role)
    );

    useEffect(() => {
        const loadSurveys = async () => {
            try {
                setLoading(true);
                setError(null);
                const params = {};
                if (currentTermCode) {
                    params.term = currentTermCode;
                }
                const data = await surveyService.getSurveys(params);
                setSurveys(data);
            } catch (_err) {
                setError('Failed to load surveys. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (!termsLoading) {
            loadSurveys();
        }
    }, [currentTermCode, termsLoading]);

    const formatDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const getSurveyStatus = (survey) => {
        if (survey.hasResponded) {
            return { bg: 'success', label: 'Completed' };
        }
        const now = new Date();
        if (survey.closeDate && new Date(survey.closeDate) < now) {
            return { bg: 'secondary', label: 'Closed' };
        }
        if (survey.openDate && new Date(survey.openDate) > now) {
            return { bg: 'info', label: 'Upcoming' };
        }
        return { bg: 'primary', label: 'Open' };
    };

    const isRelevantSurvey = (survey) => {
        if (isCoordinator) return true;
        const audience = survey.targetAudience;
        if (audience === 'all') return true;
        if (
            audience === 'faculty' &&
            userRoles.some((r) => ['faculty'].includes(r))
        ) {
            return true;
        }
        if (
            audience === 'students' &&
            userRoles.some((r) => ['student'].includes(r))
        ) {
            return true;
        }
        if (
            audience === 'peer_supporters' &&
            userRoles.some((r) => ['student'].includes(r))
        ) {
            return true;
        }
        return false;
    };

    const availableSurveys = surveys.filter(
        (s) => isRelevantSurvey(s) && !s.hasResponded && s.isActive
    );
    const completedSurveys = surveys.filter((s) => s.hasResponded);
    const manageSurveys = isCoordinator ? surveys : [];

    const renderSurveyCard = (survey, showManageActions = false) => {
        const status = getSurveyStatus(survey);
        const programConfig = survey.program
            ? PROGRAM_CONFIG[survey.program]
            : null;
        const audienceConfig =
            AUDIENCE_CONFIG[survey.targetAudience] || AUDIENCE_CONFIG.all;

        return (
            <Card key={survey._id} className="mb-3 border-0 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="d-flex align-items-start gap-2 mb-2">
                                {programConfig && (
                                    <Badge
                                        bg={programConfig.bg}
                                        text={badgeTextFor(programConfig.bg)}
                                        className={programConfig.className}
                                    >
                                        {programConfig.label}
                                    </Badge>
                                )}
                                <Badge
                                    bg={audienceConfig.bg}
                                    text={badgeTextFor(audienceConfig.bg)}
                                >
                                    {audienceConfig.label}
                                </Badge>
                                {survey.allowAnonymous && (
                                    <Badge
                                        bg="light"
                                        text="dark"
                                        className="border"
                                    >
                                        Anonymous
                                    </Badge>
                                )}
                            </div>
                            <h2 className="mb-1 fs-6">{survey.title}</h2>
                            <p className="text-muted small mb-0">
                                {survey.description}
                            </p>
                        </Col>
                        <Col md={3}>
                            {survey.openDate && (
                                <small className="text-muted d-block">
                                    Opens: {formatDate(survey.openDate)}
                                </small>
                            )}
                            {survey.closeDate && (
                                <small className="text-muted d-block">
                                    Closes: {formatDate(survey.closeDate)}
                                </small>
                            )}
                            {showManageActions && (
                                <small className="text-muted d-block mt-1">
                                    {survey.questions?.length || 0} questions
                                </small>
                            )}
                        </Col>
                        <Col md={3} className="text-md-end">
                            <div className="mb-2">
                                <Badge
                                    bg={status.bg}
                                    text={badgeTextFor(status.bg)}
                                >
                                    {status.label}
                                </Badge>
                            </div>
                            {!survey.hasResponded &&
                                survey.isActive &&
                                status.label === 'Open' && (
                                    <Button
                                        as={Link}
                                        to={`/surveys/${survey._id}`}
                                        variant="primary"
                                        size="sm"
                                    >
                                        Take Survey
                                    </Button>
                                )}
                            {survey.hasResponded && (
                                <small className="text-success d-block">
                                    Response submitted
                                </small>
                            )}
                            {showManageActions && isCoordinator && (
                                <Button
                                    as={Link}
                                    to={`/surveys/${survey._id}/results`}
                                    variant="outline-primary"
                                    size="sm"
                                    className="mt-1"
                                >
                                    View Results
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h1>Surveys</h1>
                    <p className="text-muted">
                        {isCoordinator
                            ? 'Manage and view surveys and evaluation results'
                            : 'Complete surveys and share your feedback'}
                    </p>
                </div>
                {isCoordinator && (
                    <Button as={Link} to="/surveys/new" variant="primary">
                        + Create Survey
                    </Button>
                )}
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Stats Row */}
            <Row className="mb-4">
                <Col md={4}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <p className="display-6 text-primary mb-1">
                                {availableSurveys.length}
                            </p>
                            <small className="text-muted">
                                Available Surveys
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <p className="display-6 text-success mb-1">
                                {completedSurveys.length}
                            </p>
                            <small className="text-muted">Completed</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <p className="display-6 text-info mb-1">
                                {surveys.length}
                            </p>
                            <small className="text-muted">
                                Total This Term
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {loading || termsLoading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">Loading surveys...</p>
                </div>
            ) : (
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-4"
                >
                    <Tab
                        eventKey="available"
                        title={`Available (${availableSurveys.length})`}
                    >
                        {availableSurveys.length === 0 ? (
                            <Card className="border-0 bg-light">
                                <Card.Body className="text-center py-5">
                                    <p className="text-muted mb-0">
                                        No surveys are currently available for
                                        you to complete.
                                    </p>
                                </Card.Body>
                            </Card>
                        ) : (
                            availableSurveys.map((s) => renderSurveyCard(s))
                        )}
                    </Tab>
                    <Tab
                        eventKey="completed"
                        title={`Completed (${completedSurveys.length})`}
                    >
                        {completedSurveys.length === 0 ? (
                            <Card className="border-0 bg-light">
                                <Card.Body className="text-center py-5">
                                    <p className="text-muted mb-0">
                                        You have not completed any surveys yet.
                                    </p>
                                </Card.Body>
                            </Card>
                        ) : (
                            completedSurveys.map((s) => renderSurveyCard(s))
                        )}
                    </Tab>
                    {isCoordinator && (
                        <Tab
                            eventKey="manage"
                            title={`Manage (${manageSurveys.length})`}
                        >
                            {manageSurveys.length === 0 ? (
                                <Card className="border-0 bg-light">
                                    <Card.Body className="text-center py-5">
                                        <p className="text-muted mb-3">
                                            No surveys have been created yet.
                                        </p>
                                        <Button
                                            as={Link}
                                            to="/surveys/new"
                                            variant="primary"
                                        >
                                            Create Survey
                                        </Button>
                                    </Card.Body>
                                </Card>
                            ) : (
                                manageSurveys.map((s) =>
                                    renderSurveyCard(s, true)
                                )
                            )}
                        </Tab>
                    )}
                </Tabs>
            )}
        </Container>
    );
}

export default SurveyListPage;
